import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { categoryImages } from '@/mocks/images';

export default function ShoppingGuidePage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <Navbar />

      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="py-14 md:py-20" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="section-container max-w-3xl text-center">
            <h1 className="font-heading font-bold text-2xl md:text-4xl mb-4 text-foreground-950">
              Shopping Guide
            </h1>
            <p className="text-sm md:text-base leading-relaxed text-foreground-600 max-w-2xl mx-auto">
              Browse our complete collection of product categories available on VigorBuy Spreadsheet.
              Click any category to explore products on our main website.
            </p>
          </div>
        </section>

        {/* Category List with Images */}
        <section className="py-12 md:py-16">
          <div className="section-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group rounded-lg overflow-hidden transition-all duration-300 cursor-pointer flex flex-col"
                  style={{ backgroundColor: 'oklch(var(--background-100))' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="w-full aspect-square overflow-hidden">
                    <img
                      src={categoryImages[cat.name] || ''}
                      alt={`${cat.name} - VigorBuy Spreadsheet`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1">
                      {cat.name}
                    </h3>
                    <span className="text-xs text-foreground-500 flex items-center justify-center gap-1">
                      <i className="ri-arrow-right-up-line"></i>
                      Shop Now
                    </span>
                  </div>
                </a>
              ))}
            </div>

            <div className="text-center mt-10">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary text-sm px-10 py-3.5 rounded-lg"
              >
                <i className="ri-shopping-bag-line mr-2"></i>
                Buy It Now on VigorBuy Spreadsheet
              </a>
            </div>
          </div>
        </section>

        {/* How to Shop */}
        <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="section-container max-w-3xl">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-center mb-8 text-foreground-950">
              How to Shop
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-5 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                  <i className="ri-search-line text-xl" style={{ color: 'oklch(var(--primary-500))' }}></i>
                </div>
                <h4 className="font-heading font-semibold text-sm mb-2 text-foreground-900">1. Browse Categories</h4>
                <p className="text-xs text-foreground-600">Explore our product categories and find what you need</p>
              </div>
              <div className="text-center p-5 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
                  <i className="ri-shopping-cart-line text-xl" style={{ color: 'oklch(var(--accent-600))' }}></i>
                </div>
                <h4 className="font-heading font-semibold text-sm mb-2 text-foreground-900">2. Add to Cart</h4>
                <p className="text-xs text-foreground-600">Select your favorite items and add them to your shopping cart</p>
              </div>
              <div className="text-center p-5 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--secondary-100))' }}>
                  <i className="ri-check-double-line text-xl" style={{ color: 'oklch(var(--secondary-600))' }}></i>
                </div>
                <h4 className="font-heading font-semibold text-sm mb-2 text-foreground-900">3. Checkout</h4>
                <p className="text-xs text-foreground-600">Complete your purchase securely and track your order</p>
              </div>
            </div>

            <div className="text-center mt-8">
              <Link to="/" className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-200 border-2" style={{ borderColor: 'oklch(var(--primary-300))', color: 'oklch(var(--primary-600))' }}>
                <i className="ri-home-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Shopping Guide - VigorBuy Spreadsheet',
            description: 'Browse our complete collection of product categories available on VigorBuy Spreadsheet. Click any category to explore products on our main website.',
            url: import.meta.env.VITE_SITE_URL + '/shopping-guide',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: import.meta.env.VITE_SITE_URL,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Shopping Guide',
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}